/**
 * Cumulative Deviation Graph Component - GCP Style
 * Renders an interactive chart showing the cumulative deviation with cocoon bounds
 * Similar to Global Consciousness Project visualization
 */

interface GraphData {
  timestamps: number[];
  values: number[];
  sampleRate: number;   // How many bits each data point represents
  totalBits?: number;   // Actual total bit count (optional, can calculate from array length * sampleRate)
}

// Oracle Theme Colors (matching Flutter app)
const COLORS = {
  gold: '#D4A84B',           // Main deviation line
  amber: '#E6A825',          // 95% confidence (p ≤ 0.05)
  lightPurple: '#8B6BB5',    // 99% confidence (p ≤ 0.01)
  textSecondary: '#B8A8C8',  // Muted lavender
  textMuted: '#7A6B8A',      // Muted purple
  borderColor: '#3D3456',    // Grid lines
  backgroundDark: '#0D0B14', // Near black with purple tint
};

/**
 * Format number with K/M suffix
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Render the cumulative deviation graph - GCP style
 */
export function renderDeviationGraph(
  graphData: GraphData,
  container: HTMLElement,
  totalBits?: number
): void {
  // Handle missing or invalid graphData
  if (!graphData || !graphData.timestamps || !graphData.values) {
    container.innerHTML = `
      <div class="card">
        <h3 class="card-title">Cumulative Deviation</h3>
        <p style="color: var(--color-text-secondary); text-align: center; padding: 40px;">
          No graph data available
        </p>
      </div>
    `;
    return;
  }

  const { timestamps, values } = graphData;
  // Use provided totalBits, or from graphData, or estimate from array length
  let actualTotalBits = totalBits || graphData.totalBits || values.length || 1000;
  if (actualTotalBits === 0) {
    console.warn('No totalBits for cocoon calculation, using 1000');
    actualTotalBits = 1000;
  }

  if (!timestamps.length || !values.length) {
    container.innerHTML = `
      <div class="card">
        <h3 class="card-title">Cumulative Deviation</h3>
        <p style="color: var(--color-text-secondary); text-align: center; padding: 40px;">
          No graph data available
        </p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="card">
      <h3 class="card-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
        Cumulative Deviation (GCP Style)
      </h3>
      <div class="graph-container">
        <canvas id="deviation-canvas" class="graph-canvas"></canvas>
      </div>
      <div class="graph-legend" style="display: flex; flex-wrap: wrap; justify-content: center; gap: 16px; margin-top: 12px; font-size: 11px;">
        <span style="display: flex; align-items: center; gap: 4px;">
          <span style="width: 16px; height: 3px; background: ${COLORS.gold}; border-radius: 1px;"></span>
          <span style="color: ${COLORS.textSecondary};">Cumulative Deviation</span>
        </span>
        <span style="display: flex; align-items: center; gap: 4px;">
          <span style="width: 16px; height: 3px; background: ${COLORS.amber}; border-radius: 1px;"></span>
          <span style="color: ${COLORS.textSecondary};">p ≤ 0.05 (95%)</span>
        </span>
        <span style="display: flex; align-items: center; gap: 4px;">
          <span style="width: 16px; height: 3px; background: ${COLORS.lightPurple}; border-radius: 1px;"></span>
          <span style="color: ${COLORS.textSecondary};">p ≤ 0.01 (99%)</span>
        </span>
      </div>
    </div>
  `;

  // Draw the graph on canvas after ensuring DOM is ready
  // Uses retry mechanism to handle cases where layout isn't complete on first frame
  const attemptDraw = (retriesLeft: number = 10): void => {
    const canvas = document.getElementById('deviation-canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('DeviationGraph: Canvas element not found');
      return;
    }

    let rect = canvas.getBoundingClientRect();

    // If dimensions are 0, try getting from parent container
    if (rect.width === 0 || rect.height === 0) {
      const container = canvas.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        if (containerRect.width > 0 && containerRect.height > 0) {
          // Force explicit dimensions on canvas
          canvas.style.width = containerRect.width + 'px';
          canvas.style.height = containerRect.height + 'px';
          // Re-read rect after setting dimensions
          rect = canvas.getBoundingClientRect();
        }
      }
    }

    // If still 0 dimensions, retry after another frame (up to 10 times)
    if ((rect.width === 0 || rect.height === 0) && retriesLeft > 0) {
      setTimeout(() => attemptDraw(retriesLeft - 1), 50);
      return;
    }

    // Ultimate fallback: use hardcoded default dimensions matching CSS
    if (rect.width === 0 || rect.height === 0) {
      console.warn('DeviationGraph: Using fallback dimensions 800x300');
      canvas.style.width = '800px';
      canvas.style.height = '300px';
      canvas.width = 800 * (window.devicePixelRatio || 1);
      canvas.height = 300 * (window.devicePixelRatio || 1);
    }

    drawGCPGraph(canvas, graphData, actualTotalBits);
  };

  // Use setTimeout to ensure DOM has fully rendered
  setTimeout(() => attemptDraw(), 100);
}

/**
 * Draw GCP-style graph with cocoon bounds on canvas
 */
function drawGCPGraph(
  canvas: HTMLCanvasElement,
  graphData: GraphData,
  _totalBits: number  // Kept for API compatibility, not used in cocoon calculation
): void {
  const { values } = graphData;
  // Note: sampleRate is available in graphData but not used for web display
  // The web display uses sample-level cocoon (z * sqrt(n)) to match the ±1 per sample values
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Set canvas size with device pixel ratio for sharpness
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;
  const padding = { top: 16, right: 16, bottom: 30, left: 50 };

  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  if (graphWidth <= 0 || graphHeight <= 0) {
    console.warn('DeviationGraph: Invalid graph dimensions', { width, height, graphWidth, graphHeight });
    return;
  }

  const n = values.length;
  const sampleRate = graphData.sampleRate || 1;

  // Detect data format: new format uses ±0.5 per bit, old format uses ±1 per sample
  // New format data typically has decimal values (e.g., 0.5, -1.5, 2.0)
  // Old format data typically has integer values (e.g., 1, -1, 2, -2)
  // We can detect by checking if values have fractional parts
  const hasDecimalValues = values.some(v => v !== Math.floor(v));

  // For old ±1 data, we need to scale to match the cocoon which is based on bits
  // Scale factor = sqrt(sampleRate * 0.25) = 0.5 * sqrt(sampleRate)
  const dataScaleFactor = hasDecimalValues ? 1 : (0.5 * Math.sqrt(sampleRate));

  // Calculate min/max Y including both SCALED data values AND cocoon bounds
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < n; i++) {
    // Scale data value if using old format
    const scaledDataValue = values[i] * dataScaleFactor;
    // Match Flutter: bitIndex = (i + 1) * sampleRate
    const bitIndex = (i + 1) * sampleRate;

    // GCP formula matching Flutter: z * sqrt(bitIndex * 0.25)
    const cocoon99 = 2.576 * Math.sqrt(bitIndex * 0.25);

    // Include scaled data value in range
    if (Number.isFinite(scaledDataValue)) {
      minY = Math.min(minY, scaledDataValue);
      maxY = Math.max(maxY, scaledDataValue);
    }

    // Include cocoon bounds in range
    minY = Math.min(minY, -cocoon99);
    maxY = Math.max(maxY, cocoon99);
  }

  // Guard against invalid ranges
  if (!Number.isFinite(minY)) minY = -10;
  if (!Number.isFinite(maxY)) maxY = 10;
  if (minY >= maxY) {
    minY = -10;
    maxY = 10;
  }

  // Add padding to Y range
  const yRange = maxY - minY || 1;
  minY -= yRange * 0.1;
  maxY += yRange * 0.1;

  const yDiff = maxY - minY;

  // Scale functions
  const scaleX = (i: number) => padding.left + (i / (n - 1 || 1)) * graphWidth;
  const scaleY = (v: number) => padding.top + (maxY - v) / yDiff * graphHeight;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Draw grid lines
  drawGrid(ctx, width, height, padding, graphWidth, graphHeight, minY, maxY);

  // Draw 99% cocoon bounds (purple dashed) - draw first so it's behind
  drawCocoonBounds(ctx, n, scaleX, scaleY, 2.576, COLORS.lightPurple, sampleRate);

  // Draw 95% cocoon bounds (amber dashed)
  drawCocoonBounds(ctx, n, scaleX, scaleY, 1.96, COLORS.amber, sampleRate);

  // Draw zero line
  const zeroY = scaleY(0);
  if (zeroY > padding.top && zeroY < height - padding.bottom) {
    ctx.strokeStyle = COLORS.textMuted;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding.left, zeroY);
    ctx.lineTo(width - padding.right, zeroY);
    ctx.stroke();
  }

  // Draw cumulative deviation line (gold) - scale values from sample-space to bit-space
  ctx.beginPath();
  ctx.moveTo(scaleX(0), scaleY(values[0] * dataScaleFactor));
  for (let i = 1; i < n; i++) {
    ctx.lineTo(scaleX(i), scaleY(values[i] * dataScaleFactor));
  }
  ctx.strokeStyle = COLORS.gold;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();

  // Draw Y-axis title (rotated)
  ctx.save();
  ctx.translate(12, height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = COLORS.textSecondary;
  ctx.font = '10px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Cumulative Deviation', 0, 0);
  ctx.restore();

  // Draw X-axis label (bit count, matching Flutter)
  const totalBitCount = n * sampleRate;
  ctx.fillStyle = COLORS.textSecondary;
  ctx.font = '10px Inter, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(`Bits (${formatNumber(totalBitCount)})`, width - padding.right, height - padding.bottom + 20);
}

/**
 * Draw grid lines and Y-axis labels
 */
function drawGrid(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  padding: { top: number; right: number; bottom: number; left: number },
  graphWidth: number,
  graphHeight: number,
  minY: number,
  maxY: number
): void {
  ctx.strokeStyle = COLORS.borderColor + '50'; // 50% opacity
  ctx.lineWidth = 0.5;

  const numHLines = 5;
  const numVLines = 5;

  // Horizontal grid lines
  for (let i = 0; i <= numHLines; i++) {
    const y = padding.top + (graphHeight * i / numHLines);
    ctx.beginPath();
    ctx.moveTo(padding.left, y);
    ctx.lineTo(width - padding.right, y);
    ctx.stroke();

    // Y-axis labels
    const value = maxY - (maxY - minY) * i / numHLines;
    ctx.fillStyle = COLORS.textSecondary;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(value.toFixed(0), padding.left - 8, y + 3);
  }

  // Vertical grid lines
  for (let i = 0; i <= numVLines; i++) {
    const x = padding.left + (graphWidth * i / numVLines);
    ctx.beginPath();
    ctx.moveTo(x, padding.top);
    ctx.lineTo(x, height - padding.bottom);
    ctx.stroke();
  }
}

/**
 * Draw dashed cocoon bounds (confidence interval parabolas)
 *
 * Matches Flutter formula exactly:
 * bitIndex = (sampleIndex) * sampleRate
 * cocoon = z * sqrt(bitIndex * 0.25)
 *
 * This represents the statistical significance threshold based on total bits,
 * matching how Flutter displays the same data.
 */
function drawCocoonBounds(
  ctx: CanvasRenderingContext2D,
  numPoints: number,
  scaleX: (i: number) => number,
  scaleY: (v: number) => number,
  zValue: number,
  color: string,
  sampleRate: number = 1
): void {
  if (numPoints === 0) return;

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 3]); // Dashed line

  // Upper bound
  ctx.beginPath();
  for (let i = 0; i < numPoints; i++) {
    // Match Flutter: bitIndex = (i + 1) * sampleRate
    const bitIndex = (i + 1) * sampleRate;
    // GCP formula matching Flutter: z * sqrt(bitIndex * 0.25)
    const cocoonValue = zValue * Math.sqrt(bitIndex * 0.25);
    const x = scaleX(i);
    const y = scaleY(cocoonValue);

    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();

  // Lower bound
  ctx.beginPath();
  for (let i = 0; i < numPoints; i++) {
    const bitIndex = (i + 1) * sampleRate;
    const cocoonValue = zValue * Math.sqrt(bitIndex * 0.25);
    const x = scaleX(i);
    const y = scaleY(-cocoonValue);

    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();

  ctx.setLineDash([]); // Reset to solid line
}
